import React, { useCallback } from 'react';

import { IResource } from '../ResourceCard';
import { ResourceContentDescription } from './styled';

const MAX_DESCRIPTION_PREVIEW_LENGTH = 900;

function truncateHtmlPreservingTags(html: string, maxLength: number) {
  const sourceContainer = document.createElement('div');
  const targetContainer = document.createElement('div');

  sourceContainer.innerHTML = html;

  let remainingLength = maxLength;
  let wasTruncated = false;

  const cloneChildren = (sourceNode: Node, targetNode: Node) => {
    for (const childNode of Array.from(sourceNode.childNodes)) {
      if (remainingLength === 0) {
        wasTruncated = true;
        break;
      }

      if (childNode.nodeType === Node.TEXT_NODE) {
        const textContent = childNode.textContent || '';

        if (textContent.length <= remainingLength) {
          targetNode.appendChild(document.createTextNode(textContent));
          remainingLength -= textContent.length;
          continue;
        }

        targetNode.appendChild(document.createTextNode(textContent.slice(0, remainingLength)));
        remainingLength = 0;
        wasTruncated = true;
        break;
      }

      if (childNode.nodeType === Node.ELEMENT_NODE) {
        const clonedChildNode = childNode.cloneNode(false);
        targetNode.appendChild(clonedChildNode);
        cloneChildren(childNode, clonedChildNode);

        if (!clonedChildNode.hasChildNodes() && !(clonedChildNode instanceof HTMLBRElement)) {
          clonedChildNode.remove();
        }

        if (remainingLength === 0) {
          break;
        }
      }
    }
  };

  cloneChildren(sourceContainer, targetContainer);

  return {
    wasTruncated,
    previewHtml: targetContainer.innerHTML,
  };
}

interface ResourcesDescriptionProps {
  resource: IResource;
}

export default function ResourcesDescription({ resource }: ResourcesDescriptionProps) {
  const resourceDescriptionRef = useCallback(
    (node: HTMLDivElement) => {
      if (node) {
        const { previewHtml, wasTruncated } = truncateHtmlPreservingTags(
          resource?.long_description || '',
          MAX_DESCRIPTION_PREVIEW_LENGTH
        );

        if (wasTruncated) {
          const previewContainer = document.createElement('div');
          previewContainer.innerHTML = previewHtml;

          const readMore = document.createElement('span');
          readMore.className = 'read-more';
          readMore.textContent = ' More';

          const readMoreContainer =
            previewContainer.querySelector('p:last-of-type') || previewContainer;
          readMoreContainer.appendChild(readMore);

          node.replaceChildren(...Array.from(previewContainer.childNodes));

          readMore.onclick = () => {
            const longDescriptionContainer = document.createElement('div');
            longDescriptionContainer.innerHTML = resource?.long_description || '';
            node.replaceChildren(...Array.from(longDescriptionContainer.childNodes));
          };
        }
      }
    },
    [resource?.long_description]
  );

  if (!resource?.long_description) return null;

  return (
    <ResourceContentDescription
      dangerouslySetInnerHTML={{ __html: resource.long_description }}
      ref={resourceDescriptionRef}
    />
  );
}
